import {
  GameType,
  TrackingPageGameBlock,
} from "@redotech/redo-model/order-tracking/tracking-page-definition";
import { GameObj, KAPLAYCtx } from "kaplay";
import { TrackingPageGame, VictoryState } from "./game";
import { GameCompleteCallback, GameControls } from "./game-controls";

export const flappyFreightGame: TrackingPageGame = {
  gameType: GameType.FLAPPY_FREIGHT,
  initializeGame: initializeFlappyFreightGame,
  strings: {
    victory: "🎉 Package Delivered! 🎉",
    failure: "📦 Package Lost! 📦",
  },
};

export function initializeFlappyFreightGame(
  k: KAPLAYCtx,
  onGameComplete: GameCompleteCallback,
  blockProps: TrackingPageGameBlock,
): GameControls {
  // Get canvas dimensions for scaling calculations
  const canvasWidth = k.width();
  const canvasHeight = k.height();

  // Game scaling factors - all game logic uses these relative values
  const scale = Math.min(canvasWidth / 320, canvasHeight / 480);
  const relativeWidth = (ratio: number) => canvasWidth * ratio;
  const relativeHeight = (ratio: number) => canvasHeight * ratio;
  const scaleValue = (value: number) => value * scale;

  let score = 0;
  let gameOver = false;
  let pipeSpeed = scaleValue(120);
  const pipes: GameObj[] = [];
  const basePipeSpacing = scaleValue(300); // Base distance between pipes (scaled)
  const pipeGap = scaleValue(100); // gap between upper and lower pipes (scaled)
  const jumpForce = scaleValue(300);

  // Track last pipe height for delta constraint
  let lastPipeGapY: number | null = null;
  const maxPipeHeightDelta = Math.min(relativeHeight(0.35), scaleValue(200)); // Max 35% of canvas height or 200px scaled

  // Set up gravity (scaled)
  k.setGravity(scaleValue(800));

  // Create cyberpunk background
  k.setBackground(5, 10, 20); // Dark blue-black background

  // Add scrolling grid lines for cyberpunk effect (scaled)
  const gridLines = [];
  const gridSpacing = scaleValue(30);
  const gridCount =
    Math.ceil(Math.max(canvasWidth, canvasHeight) / gridSpacing) + 5;

  for (let i = 0; i < gridCount; i++) {
    // Horizontal grid lines
    const hLine = k.add([
      k.rect(canvasWidth * 2, scaleValue(1)),
      k.pos(-canvasWidth, i * gridSpacing),
      k.color(0, 40, 80), // Dark blue grid
      k.opacity(0.3),
      "gridline",
    ]);
    gridLines.push(hLine);

    // Vertical grid lines
    const vLine = k.add([
      k.rect(scaleValue(1), canvasHeight),
      k.pos(i * scaleValue(40), 0),
      k.color(0, 40, 80), // Dark blue grid
      k.opacity(0.3),
      "gridline",
    ]);
    gridLines.push(vLine);
  }

  // Add some data stream effects (scaled)
  const streamCount = Math.max(3, Math.floor(canvasWidth / 150)); // Scale stream count with width
  for (let i = 0; i < streamCount; i++) {
    k.add([
      k.rect(scaleValue(2), canvasHeight),
      k.pos(k.rand(0, canvasWidth), 0),
      k.color(0, 255, 150), // Bright cyan
      k.opacity(0.1),
      k.move(k.rand(scaleValue(-20), scaleValue(-50)), 0),
      "datastream",
    ]);
  }

  // Create package (bird) - matching delivery-dash style with spinning (scaled)
  const birdRadius = scaleValue(12);
  const bird = k.add([
    k.circle(birdRadius), // Invisible collision circle (scaled)
    k.pos(relativeWidth(0.1), canvasHeight / 2), // 10% from left edge
    k.anchor("center"),
    k.area(),
    k.body(),
    k.opacity(0), // Make collision body invisible
    "bird",
    { wasGrounded: true },
  ]);

  // Create visual package that can rotate
  const birdVisual = k.make([k.rotate(0)]);

  // Main package body (scaled)
  const packageSize = scaleValue(24);
  birdVisual.add([
    k.rect(packageSize, packageSize),
    k.color(120, 80, 50), // Brown package color
    k.pos(0, 0),
    k.anchor("center"),
  ]);

  const tapeColor = [200, 170, 140] as const;

  // Horizontal tape stripe (scaled)
  birdVisual.add([
    k.rect(packageSize, packageSize * 0.15),
    k.color(...tapeColor),
    k.pos(0, 0),
    k.anchor("center"),
  ]);

  // Vertical tape stripe (scaled)
  birdVisual.add([
    k.rect(packageSize * 0.15, packageSize),
    k.color(...tapeColor),
    k.pos(0, 0),
    k.anchor("center"),
  ]);

  // Package label/address (red square) (scaled)
  birdVisual.add([
    k.rect(packageSize * 0.3, packageSize * 0.2),
    k.color(200, 40, 80),
    k.pos(packageSize * 0.25, -packageSize * 0.25),
    k.anchor("center"),
  ]);

  // Add wings that can flap (scaled)
  const wingScale = packageSize / 24; // Scale wings relative to package size
  const leftWing = birdVisual.add([
    k.polygon([
      k.vec2(0, 0), // Wing root
      k.vec2(-15 * wingScale, -8 * wingScale), // Wing tip top
      k.vec2(-18 * wingScale, -2 * wingScale), // Wing tip middle
      k.vec2(-15 * wingScale, 8 * wingScale), // Wing tip bottom
      k.vec2(-5 * wingScale, 6 * wingScale), // Wing root bottom
    ]),
    k.pos(-14 * wingScale, -3 * wingScale),
    k.color(200, 200, 200), // Light gray wing
    k.rotate(0),
    "wing",
  ]);

  const rightWing = birdVisual.add([
    k.polygon([
      k.vec2(0, 0), // Wing root
      k.vec2(15 * wingScale, -8 * wingScale), // Wing tip top
      k.vec2(18 * wingScale, -2 * wingScale), // Wing tip middle
      k.vec2(15 * wingScale, 8 * wingScale), // Wing tip bottom
      k.vec2(5 * wingScale, 6 * wingScale), // Wing root bottom
    ]),
    k.pos(14 * wingScale, -3 * wingScale),
    k.color(200, 200, 200), // Light gray wing
    k.rotate(0),
    "wing",
  ]);

  bird.add(birdVisual);

  // Wing flap animation variables
  let isFlapping = false;
  let flapTimer = 0;
  const flapDuration = 0.2; // 200ms flap animation
  const flapAngle = 30; // degrees to rotate wings during flap

  // Update wing animation
  bird.onUpdate(() => {
    const dt = k.dt();

    if (isFlapping) {
      flapTimer += dt;
      const progress = flapTimer / flapDuration;

      if (progress >= 1) {
        // Flap animation complete, return to neutral
        isFlapping = false;
        flapTimer = 0;
        leftWing.angle = 0;
        rightWing.angle = 0;
      } else {
        // Animate wings - create a flapping motion
        const flapProgress = Math.sin(progress * Math.PI); // Smooth up and down motion
        leftWing.angle = -flapAngle * flapProgress;
        rightWing.angle = flapAngle * flapProgress;
      }
    }

    bird.wasGrounded = bird.isGrounded();
  });

  // Cyberpunk ground/data floor (scaled)
  const groundHeight = scaleValue(20);
  const ground = k.add([
    k.rect(canvasWidth, groundHeight),
    k.pos(0, canvasHeight - groundHeight),
    k.color(10, 20, 40), // Dark blue base
    k.area(),
    k.body({ isStatic: true }),
    "ground",
  ]);

  // Ground neon accent lines (scaled)
  ground.add([
    k.rect(canvasWidth, scaleValue(2)),
    k.pos(0, 0),
    k.color(0, 255, 200), // Cyan glow on top edge
  ]);

  // Digital floor pattern (scaled)
  const floorPatternSpacing = scaleValue(20);
  for (let i = 0; i < canvasWidth / floorPatternSpacing; i++) {
    ground.add([
      k.rect(scaleValue(2), groundHeight),
      k.pos(i * floorPatternSpacing, 0),
      k.color(0, 80, 120), // Dim blue lines
      k.opacity(0.5),
    ]);
  }

  // Ceiling (invisible collision boundary) (scaled)
  const ceilingHeight = scaleValue(20);
  k.add([
    k.rect(canvasWidth, ceilingHeight),
    k.pos(0, -ceilingHeight),
    k.area(),
    k.body({ isStatic: true }),
    "ceiling",
  ]);

  // Function to create a pipe pair at specific X position
  function createPipe(xPos: number) {
    const pipeWidth = scaleValue(50); // Scaled width

    // More random positioning with better distribution (using relative height)
    const minY = pipeGap + relativeHeight(0.05); // 5% from top
    const maxY = canvasHeight - pipeGap - relativeHeight(0.07); // 7% from bottom (accounting for ground)

    let gapY: number;
    if (lastPipeGapY === null) {
      // First pipe - can be anywhere in valid range
      gapY = k.rand(minY, maxY);
    } else {
      // Constrain next pipe based on previous pipe height
      const constrainedMinY = Math.max(minY, lastPipeGapY - maxPipeHeightDelta);
      const constrainedMaxY = Math.min(maxY, lastPipeGapY + maxPipeHeightDelta);
      gapY = k.rand(constrainedMinY, constrainedMaxY);
    }

    // Update last pipe height for next iteration
    lastPipeGapY = gapY;

    // Upper digital gateway
    const upperPipe = k.add([
      k.rect(pipeWidth, gapY - pipeGap / 2),
      k.pos(xPos, 0),
      k.color(20, 25, 35), // Dark base color
      k.area(),
      k.body({ isStatic: true }),
      { scored: false }, // Add scored property as component
      "pipe",
    ]);

    // Upper pipe neon border (left edge) (scaled)
    const borderWidth = scaleValue(3);
    upperPipe.add([
      k.rect(borderWidth, gapY - pipeGap / 2),
      k.pos(0, 0),
      k.color(0, 255, 200), // Cyan glow
    ]);

    // Upper pipe neon border (right edge) (scaled)
    upperPipe.add([
      k.rect(borderWidth, gapY - pipeGap / 2),
      k.pos(pipeWidth - borderWidth, 0),
      k.color(0, 255, 200), // Cyan glow
    ]);

    // Upper pipe bottom edge (portal entrance) (scaled)
    const edgeHeight = scaleValue(4);
    upperPipe.add([
      k.rect(pipeWidth, edgeHeight),
      k.pos(0, gapY - pipeGap / 2 - edgeHeight),
      k.color(0, 150, 255), // Blue glow
    ]);

    // Circuit pattern lines in upper pipe (scaled)
    const circuitSpacing = scaleValue(20);
    const circuitMargin = scaleValue(5);
    const circuitHeight = scaleValue(2);
    for (let i = 0; i < 3; i++) {
      upperPipe.add([
        k.rect(pipeWidth - circuitMargin * 2, circuitHeight),
        k.pos(circuitMargin, (gapY - pipeGap / 2) * 0.2 + i * circuitSpacing),
        k.color(0, 100, 150), // Dimmer circuit lines
      ]);
    }

    // Lower digital gateway
    const lowerPipeHeight = canvasHeight - gapY - pipeGap / 2 - groundHeight;
    const lowerPipe = k.add([
      k.rect(pipeWidth, lowerPipeHeight),
      k.pos(xPos, gapY + pipeGap / 2),
      k.color(20, 25, 35), // Dark base color
      k.area(),
      k.body({ isStatic: true }),
      { scored: false }, // Add scored property as component
      "pipe",
    ]);

    // Lower pipe neon border (left edge) (scaled)
    lowerPipe.add([
      k.rect(borderWidth, lowerPipeHeight),
      k.pos(0, 0),
      k.color(0, 255, 200), // Cyan glow
    ]);

    // Lower pipe neon border (right edge) (scaled)
    lowerPipe.add([
      k.rect(borderWidth, lowerPipeHeight),
      k.pos(pipeWidth - borderWidth, 0),
      k.color(0, 255, 200), // Cyan glow
    ]);

    // Lower pipe top edge (portal entrance) (scaled)
    lowerPipe.add([
      k.rect(pipeWidth, edgeHeight),
      k.pos(0, 0),
      k.color(0, 150, 255), // Blue glow
    ]);

    // Circuit pattern lines in lower pipe (scaled)
    for (let i = 0; i < 3; i++) {
      lowerPipe.add([
        k.rect(pipeWidth - circuitMargin * 2, circuitHeight),
        k.pos(circuitMargin, lowerPipeHeight * 0.3 + i * circuitSpacing),
        k.color(0, 100, 150), // Dimmer circuit lines
      ]);
    }

    pipes.push(upperPipe, lowerPipe);
  }

  // Bird jump function
  function jump() {
    if (!gameOver) {
      bird.vel = k.vec2(0, -jumpForce);
      // Trigger wing flap animation
      isFlapping = true;
      flapTimer = 0;
    }
  }

  // Add canvas click listener for mobile tap-to-jump
  k.onMousePress(() => {
    jump();
  });

  // Keyboard controls for testing
  k.onKeyPress("space", () => {
    jump();
  });

  // Game update loop
  k.onUpdate(() => {
    if (gameOver) {
      return;
    }

    // Move pipes
    for (const pipe of pipes) {
      pipe.pos.x -= pipeSpeed * k.dt();
    }

    // Remove pipes that are off screen (scaled)
    const offScreenMargin = scaleValue(100);
    for (const [index, pipe] of pipes.entries()) {
      if (pipe.pos.x < -offScreenMargin) {
        k.destroy(pipe);
        pipes.splice(index, 1);
      }
    }

    // Create new pipes when the rightmost pipe gets close enough to the right edge
    const rightmostPipeX =
      pipes.length > 0
        ? Math.max(...pipes.map((p) => p.pos.x))
        : -basePipeSpacing;
    if (rightmostPipeX < canvasWidth + basePipeSpacing) {
      // Add randomness to pipe spacing - between 0.7x and 1.3x base spacing
      const randomSpacing = basePipeSpacing * k.rand(0.7, 1.3);
      createPipe(rightmostPipeX + randomSpacing);
    }

    // Increase speed gradually (scaled)
    const speedIncrement = scaleValue(2);
    const maxSpeed = scaleValue(200);
    pipeSpeed = Math.min(pipeSpeed + speedIncrement * k.dt(), maxSpeed);
  });

  function handleGameOver() {
    // Stop the game loop
    if (gameOver) {
      return;
    }
    gameOver = true;
    if (score >= 50) {
      onGameComplete({ finalScore: score, victoryState: VictoryState.VICTORY });
    } else {
      onGameComplete({ finalScore: score, victoryState: VictoryState.FAILURE });
    }
  }

  // Collision detection - package falls when hit
  bird.onCollide("pipe", handleGameOver);

  bird.onCollide("ceiling", handleGameOver);

  bird.onCollide("ground", handleGameOver);

  // Scoring system - check if bird has passed pipes
  k.onUpdate(() => {
    if (gameOver) {
      return;
    }

    // Group pipes by pairs (every 2 pipes = 1 pair)
    for (let i = 0; i < pipes.length; i += 2) {
      const upperPipe = pipes[i];
      const lowerPipe = pipes[i + 1];

      // Check if this pipe pair hasn't been scored yet and bird has passed it (scaled)
      const pipePassOffset = scaleValue(50);
      if (
        upperPipe &&
        lowerPipe &&
        !upperPipe.scored &&
        bird.pos.x > upperPipe.pos.x + pipePassOffset
      ) {
        upperPipe.scored = true;
        lowerPipe.scored = true;
        score += 10; // 10 points per pipe pair
        console.log("Score updated:", score); // Debug logging
      }
    }
  });

  // Create initial pipe at a reasonable distance from bird
  const initialPipeDistance = Math.max(scaleValue(200), relativeWidth(0.3)); // Max 200px scaled or 30% of width
  const firstPipeX = bird.pos.x + initialPipeDistance;
  createPipe(firstPipeX);

  // Return controls interface (simplified for tap-to-jump)
  return {
    moveLeft: () => {}, // No-op for flappy bird
    moveRight: () => {}, // No-op for flappy bird
    moveUp: jump, // Map up to jump for compatibility
    moveDown: () => {}, // No-op for flappy bird
    stopMovement: () => {}, // No-op for flappy bird
  };
}
